import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { mangaList, categories } from '@/mocks/mangaData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const manga = useMemo(() => mangaList.find((m) => m.id === id), [id]);

  const relatedManga = useMemo(() => {
    if (!manga) return [];
    return mangaList
      .filter((m) => m.id !== manga.id && (m.category === manga.category || m.tags.some((t) => manga.tags.includes(t))))
      .slice(0, 6);
  }, [manga]);

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-200 mb-4">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-400 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === manga.category)?.name || manga.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: manga.title,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    datePublished: manga.updateDate,
    dateModified: manga.updateDate,
    url: `https://hmanga.com/article/${manga.id}`,
    publisher: {
      '@type': 'Organization',
      name: 'h漫画',
      url: 'https://hmanga.com',
    },
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hmanga.com/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://hmanga.com/category/${manga.category}` },
      { '@type': 'ListItem', position: 3, name: manga.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryName, href: `/category/${manga.category}` },
            { label: manga.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full sm:w-64 lg:w-72 mx-auto lg:mx-0">
                <div className="rounded-lg overflow-hidden aspect-[3/4]">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    title={`${manga.title} - h漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mb-3">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-500">作者：<strong className="text-foreground-300">{manga.author}</strong></span>
                  <span className="text-foreground-600">|</span>
                  <span className="text-sm text-foreground-500">分类：<Link to={`/category/${manga.category}`} className="text-primary-500 hover:text-primary-400 cursor-pointer">{categoryName}</Link></span>
                  <span className="text-foreground-600">|</span>
                  <span className="text-sm text-foreground-500">更新：{manga.updateDate}</span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-5">
                  <div className="flex items-center gap-1">
                    <span className="text-accent-500 font-bold text-lg">{manga.rating}</span>
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-eye-line mr-1"></i>{(manga.views / 10000).toFixed(1)}万阅读
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="bg-secondary-100/80 text-secondary-700 text-xs px-3 py-1 rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 border border-background-300/60 rounded-lg p-4 md:p-5 mb-5">
                  <h2 className="text-base font-semibold text-foreground-200 font-heading mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-400 leading-relaxed">{manga.description}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex-1 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md text-base font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-book-open-line"></i>开始阅读
                  </button>
                  <button className="flex-1 bg-background-100 hover:bg-background-200 border border-background-300/60 text-foreground-200 px-6 py-3 rounded-md text-base font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-heart-line"></i>加入收藏
                  </button>
                </div>
              </div>
            </div>

            {relatedManga.length > 0 && (
              <section className="mt-12 md:mt-16">
                <h3 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-5">相关推荐</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop="">
                  {relatedManga.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - h漫画相关推荐`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <h4 className="text-xs font-medium text-foreground-300 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {item.title}
                      </h4>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}